import type { UserPublic } from "@eduviaos/shared";
import type { PortalUser } from "./sessionContext";
import type { PortalRole } from "./institution";

/**
 * Map the backend principal (`UserPublic` from `/auth/login` + `/auth/me`)
 * into the shell-facing `PortalUser` (Phase SC-3b).
 *
 * Role mapping: the backend `parent` role becomes the portal `guardian`
 * persona; centre staff (`owner`, `admin`, `teacher`, `staff`) become the
 * `team` persona (Phase SC-22d — check-in, attendance, pay, leave); every
 * other role renders as `student`. Access is enforced on the BE (permission
 * ceiling + `require_vertical` + team membership), so a mis-mapped persona
 * here can never widen what the API returns.
 *
 * `ageBand` defaults to `senior`; the real class-derived age band arrives
 * with the learning-pathway intake in a later SC slice. `children` starts
 * empty — the guardian→learners relationship is served by a dedicated API
 * (not `/auth/me`) and is out of scope for the study-centre student app.
 */
const TEAM_ROLES: ReadonlySet<UserPublic["role"]> = new Set(["owner", "admin", "teacher", "staff"]);

export function portalUserFromUser(user: UserPublic): PortalUser {
  const role: PortalRole =
    user.role === "parent" ? "guardian" : TEAM_ROLES.has(user.role) ? "team" : "student";
  const name = user.full_name?.trim() || user.email.split("@")[0] || "there";
  return {
    userId: user.id,
    name,
    avatarUrl: user.avatar_url ?? null,
    role,
    ageBand: "senior",
    children: [],
  };
}
